import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiZap, FiSave, FiCloud, FiCloudOff, FiAlertCircle } from 'react-icons/fi';
import { Spinner } from '../common/Spinner';

interface StageCompletion {
  genesis:      boolean;
  tuning:       boolean;
  artifact:     boolean;
  probes:       boolean;
  capabilities: boolean;
}

interface ForgeStateFlowProps {
  activePanel: number;
  stageCompletion: StageCompletion;
  needsRecompile: boolean;
  probesStale: boolean;
  autoMode: boolean;
  isLoading: boolean;
  isDirty: boolean;
  onStageClick: (index: number) => void;
  onAutoModeToggle: (enabled: boolean) => void;
  onRecompile: () => void;
  onSave: () => void;
  currentPersonaId: string | null;
  // Autosave
  autoSaveEnabled: boolean;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastAutoSavedAt: string | null;
  onAutoSaveToggle: (enabled: boolean) => void;
  onAutoSaveRetry: () => void;
  onOpenSaveDialog: () => void; // for new-persona nudge
}

type StageState = 'pending' | 'active' | 'complete' | 'needs_action';

interface StageConfig {
  index: number;
  label: string;
  color: string;         // Tailwind color name: blue | purple | green
  completionKey: keyof StageCompletion;
}

const STAGES: StageConfig[] = [
  { index: 0, label: 'Genesis',      color: 'blue',   completionKey: 'genesis'      },
  { index: 1, label: 'Tuning',       color: 'purple', completionKey: 'tuning'       },
  { index: 2, label: 'Artifact',     color: 'green',  completionKey: 'artifact'     },
  { index: 3, label: 'Probes',       color: 'amber',  completionKey: 'probes'       },
  { index: 4, label: 'Capabilities', color: 'cyan',   completionKey: 'capabilities' },
];

const COLOR_MAP: Record<string, Record<string, string>> = {
  blue: {
    bg:        'bg-blue-600',
    bgLight:   'bg-blue-100',
    text:      'text-blue-300',
    textLight: 'text-blue-700',
    ring:      'ring-blue-500',
    border:    'border-blue-700',
    glow:      'shadow-[0_0_10px_rgba(59,130,246,0.6)]',
  },
  purple: {
    bg:        'bg-purple-600',
    bgLight:   'bg-purple-100',
    text:      'text-purple-300',
    textLight: 'text-purple-700',
    ring:      'ring-purple-500',
    border:    'border-purple-700',
    glow:      'shadow-[0_0_10px_rgba(147,51,234,0.6)]',
  },
  green: {
    bg:        'bg-green-600',
    bgLight:   'bg-green-100',
    text:      'text-green-300',
    textLight: 'text-green-700',
    ring:      'ring-green-500',
    border:    'border-green-700',
    glow:      'shadow-[0_0_10px_rgba(34,197,94,0.6)]',
  },
  amber: {
    bg:        'bg-amber-500',
    bgLight:   'bg-amber-100',
    text:      'text-amber-300',
    textLight: 'text-amber-700',
    ring:      'ring-amber-400',
    border:    'border-amber-600',
    glow:      'shadow-[0_0_10px_rgba(245,158,11,0.6)]',
  },
  cyan: {
    bg:        'bg-cyan-600',
    bgLight:   'bg-cyan-100',
    text:      'text-cyan-300',
    textLight: 'text-cyan-700',
    ring:      'ring-cyan-500',
    border:    'border-cyan-700',
    glow:      'shadow-[0_0_10px_rgba(6,182,212,0.6)]',
  },
};

function getStagePillState(
  stage: StageConfig,
  activePanel: number,
  stageCompletion: StageCompletion,
  needsRecompile: boolean,
  probesStale: boolean,
  currentPersonaId: string | null,
): StageState {
  const isActive   = activePanel === stage.index;
  const isComplete = stageCompletion[stage.completionKey];

  // Probes pill: unsaved persona = needs_action (save required)
  if (stage.index === 3 && !currentPersonaId) return 'needs_action';
  // Probes pill: stale = needs_action even if complete
  if (stage.index === 3 && probesStale && isComplete) return 'needs_action';
  // Artifact pill: stale = needs_action when active
  if (stage.index === 2 && needsRecompile && isActive) return 'needs_action';

  if (isActive)   return 'active';
  if (isComplete) return 'complete';
  return 'pending';
}

interface StagePillProps {
  stage: StageConfig;
  pillState: StageState;
  darkMode: boolean;
  isLoading: boolean;
  onClick: () => void;
  title?: string;
}

const StagePill: React.FC<StagePillProps> = ({ stage, pillState, darkMode, isLoading, onClick, title }) => {
  const c = COLOR_MAP[stage.color];

  const baseClasses = 'relative flex items-center px-4 py-2 rounded-full font-medium text-sm cursor-pointer transition-all select-none';

  let pillClasses = baseClasses;
  let labelClasses = '';
  let ringClasses = '';

  switch (pillState) {
    case 'active':
      pillClasses += darkMode
        ? ` ${c.bg} text-white ${c.glow}`
        : ` ${c.bgLight} ${c.textLight} ${c.glow}`;
      ringClasses = `absolute inset-0 rounded-full ring-2 ${c.ring} animate-pulse pointer-events-none`;
      break;
    case 'complete':
      pillClasses += darkMode
        ? ` ${c.bg} text-white`
        : ` ${c.bgLight} ${c.textLight}`;
      break;
    case 'needs_action':
      pillClasses += ' bg-amber-500 text-white shadow-[0_0_10px_rgba(245,158,11,0.6)]';
      ringClasses = 'absolute inset-0 rounded-full ring-2 ring-amber-400 animate-pulse pointer-events-none';
      break;
    case 'pending':
    default:
      pillClasses += darkMode
        ? ' bg-gray-700 text-gray-400'
        : ' bg-gray-200 text-gray-500';
      break;
  }

  const icon = pillState === 'complete'
    ? '✓'
    : pillState === 'needs_action'
      ? '⚠'
      : pillState === 'active' && isLoading
        ? null
        : null;

  return (
    <button className={pillClasses} onClick={onClick} aria-label={`Go to ${stage.label}`} title={title}>
      {ringClasses && <span className={ringClasses} aria-hidden="true" />}
      {pillState === 'active' && isLoading
        ? <Spinner size="sm" />
        : icon && <span className="mr-1">{icon}</span>
      }
      <span>{stage.label}</span>
    </button>
  );
};

// ── AutoSave Pill ─────────────────────────────────────────────────────────────
interface AutoSavePillProps {
  enabled: boolean;
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastAutoSavedAt: string | null;
  hasPersonaId: boolean;
  darkMode: boolean;
  onToggle: (enabled: boolean) => void;
  onRetry: () => void;
  onOpenSaveDialog: () => void;
}

const AutoSavePill: React.FC<AutoSavePillProps> = ({
  enabled,
  status,
  lastAutoSavedAt,
  hasPersonaId,
  darkMode,
  onToggle,
  onRetry,
  onOpenSaveDialog,
}) => {
  // Needs-first-save state: autosave is on but no persona ID yet
  const needsFirstSave = enabled && !hasPersonaId;

  // Label + icon + color
  let icon: React.ReactNode;
  let label: string;
  let pillClass: string;
  let clickAction: (() => void) | undefined;
  let title: string;

  if (!enabled) {
    icon = <FiCloudOff size={13} />;
    label = 'Autosave off';
    pillClass = darkMode
      ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
      : 'bg-gray-200 text-gray-500 hover:bg-gray-300';
    clickAction = () => onToggle(true);
    title = 'Autosave is disabled. Click to enable — changes will be saved automatically on tab switch.';
  } else if (needsFirstSave) {
    icon = <FiCloud size={13} className="text-amber-400" />;
    label = 'Autosave · save first →';
    pillClass = darkMode
      ? 'bg-amber-900/40 text-amber-300 hover:bg-amber-800/50 ring-1 ring-amber-600'
      : 'bg-amber-50 text-amber-700 hover:bg-amber-100 ring-1 ring-amber-400';
    clickAction = onOpenSaveDialog;
    title = 'Autosave is on but needs a first manual save to activate. Click to save now.';
  } else if (status === 'saving') {
    icon = <Spinner size="sm" />;
    label = 'Saving…';
    pillClass = darkMode
      ? 'bg-blue-900/40 text-blue-300 ring-1 ring-blue-600 animate-pulse'
      : 'bg-blue-50 text-blue-700 ring-1 ring-blue-400 animate-pulse';
    title = 'Autosaving your persona…';
  } else if (status === 'saved') {
    icon = <FiCloud size={13} className="text-green-400" />;
    label = lastAutoSavedAt ? `Autosaved · ${relativeTimeSince(lastAutoSavedAt)}` : 'Autosaved';
    pillClass = darkMode
      ? 'bg-green-900/40 text-green-300 ring-1 ring-green-700'
      : 'bg-green-50 text-green-700 ring-1 ring-green-300';
    clickAction = () => onToggle(false);
    title = 'Autosave is on and up to date. Click to disable.';
  } else if (status === 'error') {
    icon = <FiAlertCircle size={13} className="text-red-400" />;
    label = 'Autosave failed · retry';
    pillClass = darkMode
      ? 'bg-red-900/40 text-red-300 ring-1 ring-red-700 hover:bg-red-800/50'
      : 'bg-red-50 text-red-700 ring-1 ring-red-300 hover:bg-red-100';
    clickAction = onRetry;
    title = 'Autosave failed. Click to retry.';
  } else {
    // idle + enabled + has persona
    icon = <FiCloud size={13} className={darkMode ? 'text-blue-400' : 'text-blue-500'} />;
    label = lastAutoSavedAt
      ? `Autosave · ${relativeTimeSince(lastAutoSavedAt)}`
      : 'Autosave on';
    pillClass = darkMode
      ? 'bg-blue-900/30 text-blue-300 ring-1 ring-blue-800 hover:bg-blue-900/50'
      : 'bg-blue-50 text-blue-600 ring-1 ring-blue-200 hover:bg-blue-100';
    clickAction = () => onToggle(false);
    title = 'Autosave is on — changes are saved automatically on tab switch. Click to disable.';
  }

  return (
    <button
      onClick={clickAction}
      title={title}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 select-none ${pillClass}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

// ── Relative time helper ──────────────────────────────────────────────────────
function relativeTimeSince(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

const ForgeStateFlow: React.FC<ForgeStateFlowProps> = ({
  activePanel,
  stageCompletion,
  needsRecompile,
  probesStale,
  autoMode,
  isLoading,
  isDirty,
  onStageClick,
  onAutoModeToggle,
  onRecompile,
  onSave,
  currentPersonaId,
  autoSaveEnabled,
  autoSaveStatus,
  lastAutoSavedAt,
  onAutoSaveToggle,
  onAutoSaveRetry,
  onOpenSaveDialog,
}) => {
  const { darkMode } = useTheme();

  return (
    <div className="flex flex-col gap-1 w-full">
      {/* Main pipeline row */}
      <div className="flex items-center gap-3 flex-wrap">

        {/* Auto/Manual toggle */}
        <button
          onClick={() => onAutoModeToggle(!autoMode)}
          title={autoMode
            ? 'Auto mode ON — if source or controls have changed, the artifact will recompile when you open the Artifact tab. Toggle off for full manual control.'
            : 'Manual mode — you control when compilation runs. Toggle on to let the forge compile automatically.'}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            autoMode
              ? darkMode
                ? 'bg-green-800/50 text-green-300 ring-1 ring-green-600'
                : 'bg-green-100 text-green-700 ring-1 ring-green-400'
              : darkMode
                ? 'bg-gray-700 text-gray-400'
                : 'bg-gray-200 text-gray-500'
          }`}
        >
          <FiZap className={autoMode ? 'text-yellow-400' : ''} size={14} />
          <span>{autoMode ? 'Auto' : 'Manual'}</span>
        </button>

        {/* Connector */}
        <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>──</span>

        {/* Stage pills */}
        {STAGES.map((stage, i) => {
          const pillState = getStagePillState(stage, activePanel, stageCompletion, needsRecompile, probesStale, currentPersonaId);
          return (
            <React.Fragment key={stage.index}>
              <StagePill
                stage={stage}
                pillState={pillState}
                darkMode={darkMode}
                isLoading={isLoading}
                onClick={() => onStageClick(stage.index)}
                title={
                  stage.index === 0 ? 'Source — define the character bible, tethics, patterns and antipatterns'
                  : stage.index === 1 ? 'Tuning — generate and adjust the control panel sliders and toggles'
                  : stage.index === 2 ? 'Artifact — compile the system prompt from source + controls'
                  : stage.index === 3 && !currentPersonaId ? '⚠ Save your persona first — probes require a saved persona in the database'
                  : stage.index === 3 ? 'Probes — generate and run diagnostic test cases against the compiled artifact'
                  : 'Capabilities — view and edit capability ratings extracted from the compiled artifact'
                }
              />
              {i < STAGES.length - 1 && (
                <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>──→</span>
              )}
            </React.Fragment>
          );
        })}

        {/* Connector */}
        <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>──</span>

        {/* Save/Update — 1st class citizen */}
        <div className="relative group ml-2">
          <button
            onClick={onSave}
            className={`relative flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold shadow-md transition-all duration-200 ${
              isDirty
                ? darkMode
                  ? 'bg-amber-500 hover:bg-amber-400 text-white shadow-amber-900/40'
                  : 'bg-amber-500 hover:bg-amber-400 text-white shadow-amber-300/60'
                : darkMode
                  ? 'bg-green-600 hover:bg-green-500 text-white shadow-green-900/40'
                  : 'bg-green-500 hover:bg-green-400 text-white shadow-green-300/60'
            } ${isDirty ? 'ring-2 ring-offset-1 ring-amber-400 animate-pulse' : ''}`}
          >
            <FiSave size={16} />
            <span>{currentPersonaId ? 'Update' : 'Save'}</span>
            {isDirty && (
              <span className="ml-1 text-xs font-normal opacity-80">●</span>
            )}
          </button>

          {/* Tooltip */}
          <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 rounded-lg text-xs shadow-xl pointer-events-none
            opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 ${
            darkMode ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-white text-gray-700 border border-gray-200'
          }`}>
            {isDirty && needsRecompile
              ? '⚠ You have unsaved changes and a stale artifact. Consider compiling before saving so the stored prompt is up to date.'
              : isDirty
                ? '💾 You have unsaved changes. Click to persist your work to the database.'
                : currentPersonaId
                  ? '✓ Persona is saved and up to date. Click to save any future changes.'
                  : '💾 Save this persona to the database so you can use it in sessions and share it.'}
            {/* Caret */}
            <div className={`absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent ${
              darkMode ? 'border-t-gray-900' : 'border-t-white'
            }`} />
          </div>
        </div>
        {/* ── Autosave pill ─────────────────────────────────────────────── */}
        <AutoSavePill
          enabled={autoSaveEnabled}
          status={autoSaveStatus}
          lastAutoSavedAt={lastAutoSavedAt}
          hasPersonaId={!!currentPersonaId}
          darkMode={darkMode}
          onToggle={onAutoSaveToggle}
          onRetry={onAutoSaveRetry}
          onOpenSaveDialog={onOpenSaveDialog}
        />

      </div>

      {/* Amber recompile banner */}
      {needsRecompile && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${
          darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-700'
        }`}>
          {autoMode ? (
            <span>⚡ Will auto-compile when you open Verification</span>
          ) : (
            <>
              <span>⚠ Recompile needed</span>
              <button
                onClick={onRecompile}
                disabled={isLoading}
                className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                  darkMode
                    ? 'bg-amber-700 hover:bg-amber-600 text-white'
                    : 'bg-amber-200 hover:bg-amber-300 text-amber-800'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading ? <Spinner size="sm" /> : 'Compile Now'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ForgeStateFlow;
